import { API } from '@/api/API';
import { cartApiParams } from '@/api/Cart';
import { completedOrdersRequestEmbeds, } from '@/checkout/helpers/completing';
import TixStripeError from '@/checkout/stripe/TixStripeError';
import { isLoggedIn } from '@/state/Authentication';
import store from '@/store/store';
export async function processPaymentPayloads(cart, stripe, payloads, options, elements, confirmPaymentData) {
    if (payloads.length < 1) {
        throw new Error('Invalid payments array; At least one payload is required to make a payment');
    }
    const params = { cart, stripe, options, elements, confirmPaymentData };
    const copiedPaylods = [...payloads];
    const last = copiedPaylods.pop();
    await processFirstNPayments(copiedPaylods, params);
    return await processLastPayment(last, params);
}
async function processFirstNPayments(payloads, options) {
    for (const payload of payloads) {
        const { cart } = await processPartialPayment(payload, options);
        await store.dispatch('Cart/injectResponse', cart);
    }
}
async function processLastPayment(payment, options) {
    const amount = payment.gateway_id === 'free' ? 0 : Number(payment.amount);
    const due = store.getters['Cart/paymentDue'];
    if (amount < due) {
        return await processPartialPayment(payment, options);
    }
    else {
        return await processCheckout(payment, options);
    }
}
function processPartialPayment(payload, options) {
    return partialPay(options.cart.id, payload).catch((error) => {
        return handleRequiredPaymentIntent(error, payload, options).then((newPayload) => {
            return partialPay(options.cart.id, newPayload);
        });
    });
}
async function partialPay(id, payload) {
    const endpoint = isLoggedIn() ? `my/cart/${id}/pay` : `cart/${id}/pay`;
    const { query, embed } = cartApiParams();
    // TODO `pay` api rejects a payload with `captcha` property. Remove this once `captcha`
    // is enabled for that endpoint or it is removed altogether.
    const body = { ...payload, captcha: undefined };
    const response = await API.post(endpoint, { body, query, embed });
    return {
        status: 'partial',
        cart: response,
    };
}
function processCheckout(payload, options) {
    return checkout(options.cart.id, payload, options.options).catch((error) => {
        return handleRequiredPaymentIntent(error, payload, options).then((newPayload) => {
            return checkout(options.cart.id, newPayload, options.options);
        });
    });
}
export function checkout(id, payment, options) {
    if (options.injected) {
        return checkoutInjectedCart(id, payment, options);
    }
    else if (isLoggedIn()) {
        return checkoutAuthenticatedCart(id, payment, options);
    }
    else {
        return checkoutGuestCart(id, payment, options);
    }
}
function checkoutInjectedCart(id, payment, options) {
    // No `my/` prefix.
    return post(`cart/${id}/guest_checkout`, payment, {
        send_email: !options.suppressEmailNotification,
        send_sms_to: options.sendSmsTo,
    });
}
function checkoutGuestCart(id, payment, options) {
    const path = `my/cart/${id}/guest_checkout`;
    if (!options.giftee) {
        return post(path, payment, { guest_identity_id: options.purchaser, send_sms_to: options.sendSmsTo });
    }
    else {
        return post(path, payment, {
            guest_identity_id: options.giftee,
            giver_identity_id: options.purchaser,
            send_sms_to: options.sendSmsTo,
        });
    }
}
function checkoutAuthenticatedCart(id, payment, options) {
    if (!options.giftee) {
        return post(`my/cart/${id}/checkout`, payment, { send_sms_to: options.sendSmsTo });
    }
    else {
        return post(`my/cart/${id}/gift_checkout`, payment, {
            giftee_identity_id: options.giftee,
            send_sms_to: options.sendSmsTo,
        });
    }
}
function handleRequiredPaymentIntent(error, payload, { stripe, elements, confirmPaymentData }) {
    if (error.response) {
        const { status, data } = error.response;
        if (status === 402 && data._data[0]._code === 'intent_requires_action') {
            return stripe
                .confirmPayment({
                elements,
                clientSecret: data._data[0]._extra.client_secret,
                confirmParams: confirmPaymentData,
                redirect: 'if_required',
            })
                .then((result) => {
                if (result.paymentIntent) {
                    const newPayload = { ...payload };
                    newPayload.gateway_data.source = result.paymentIntent.id;
                    return newPayload;
                }
                else {
                    throw new TixStripeError(result.error);
                }
            });
        }
    }
    // Rethrow any other errors.
    throw error;
}
function post(path, payment, identities = {}) {
    const body = { ...payment, ...identities };
    // TODO Optimize: Only fetch the ticket_order, seller, and seller.meta.
    // Pass the cart and purchaser email address from <CheckoutRoute> to <CompletedOrdersRoute>.
    const embed = ['ticket_order', ...completedOrdersRequestEmbeds];
    return API.post(path, { body, embed }).then((response) => {
        // TODO Figure out a way to do better type support for this or have the API return a more expected response.
        // @ts-expect-error Response can be `{ _data: { _code: 'intent_processing' }[] }` for BACS checkout.
        if ('_data' in response && response?._data[0]._code === 'intent_processing') {
            return {
                status: 'pending',
                cart: store.getters['Cart/cart'],
            };
        }
        else {
            return {
                status: 'completed',
                orders: response,
            };
        }
    });
}
